import * as i0 from '@angular/core';
import { Injectable, Inject, EventEmitter, Component, ViewEncapsulation, Input, Output, HostListener, Pipe, NgModule } from '@angular/core';
import { DragBox, Draw, Select, defaults, Extent } from 'ol/interaction';
import { BehaviorSubject, Subscription, Subject, combineLatest, timer, of, forkJoin, from, merge, EMPTY } from 'rxjs';
import * as i5 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@anb/ng-map-config-manager';
import { LegendTypeEnum } from '@anb/ng-map-config-manager';
import { faLayerGroup, faChevronUp, faChevronDown, faSuitcase } from '@fortawesome/free-solid-svg-icons';
import { isEmpty } from 'lodash-es';
import { View } from 'ol';
import Map from 'ol/Map';
import { defaults as defaults$1, Control } from 'ol/control';
import { get } from 'ol/proj';
import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';
import { shareReplay, switchMap, filter, debounce, tap, distinctUntilChanged, map, mergeMap, catchError, finalize, withLatestFrom } from 'rxjs/operators';
import { extend } from 'ol/extent';
import * as i4 from '@angular/common/http';
import * as i6 from '@fortawesome/angular-fontawesome';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AnbUIElementsModule } from '@anb/ng-ui';
import EsriJSON from 'ol/format/EsriJSON';
import WKT from 'ol/format/WKT';
import GeoJSON from 'ol/format/GeoJSON';
import { ImageArcGISRest } from 'ol/source';

var ButtonStatus;
(function (ButtonStatus) {
    ButtonStatus["ACTIVE"] = "active";
    ButtonStatus["DISABLED"] = "disabled";
    ButtonStatus["NORMAL"] = "normal";
})(ButtonStatus || (ButtonStatus = {}));

var Projection;
(function (Projection) {
    Projection["_31370"] = "_31370";
    Projection["_4326"] = "_4326";
})(Projection || (Projection = {}));

var ESRISpatialRelationship;
(function (ESRISpatialRelationship) {
    ESRISpatialRelationship["INTERSECTS"] = "esriSpatialRelIntersects";
    ESRISpatialRelationship["CONTAINS"] = "esriSpatialRelContains";
    ESRISpatialRelationship["RELCROSS"] = "esriSpatialRelCrosses";
    ESRISpatialRelationship["ENVELOPEINTERSECTS"] = "esriSpatialRelEnvelopeIntersects";
    ESRISpatialRelationship["INDEXINTERSECTS"] = "esriSpatialRelIndexIntersects";
    ESRISpatialRelationship["OVERLAPS"] = "esriSpatialRelOverlaps";
    ESRISpatialRelationship["TOUCHES"] = "esriSpatialRelTouches";
    ESRISpatialRelationship["WITHIN"] = "esriSpatialRelWithin";
    ESRISpatialRelationship["RELATION"] = "esriSpatialRelRelation";
})(ESRISpatialRelationship || (ESRISpatialRelationship = {}));

class ANBDragBox extends DragBox {
    constructor(options) {
        super(options);
        this.options = options;
        this.onChange = new BehaviorSubject(null);
        this.onChangeActive = new BehaviorSubject(null);
        this.onBoxDrag = new BehaviorSubject(null);
        this.onBoxStart = new BehaviorSubject(null);
        this.onbBoxEnd = new BehaviorSubject(null);
        this.onPropertyChange = new BehaviorSubject(null);
        this.on('boxdrag', this.boxDragFunction.bind(this));
        this.on('boxend', this.boxEndFunction.bind(this));
        this.on('change', this.changeFunction.bind(this));
        this.on('change:active', this.onChangeActiveFunction.bind(this));
        this.on('propertychange', this.onPropertyChangeFunction.bind(this));
    }
    ngOnDestroy() {
        this.un('boxdrag', this.boxDragFunction);
        this.un('boxend', this.boxEndFunction);
        this.un('change:active', this.onChangeActiveFunction);
        this.un('propertychange', this.onPropertyChangeFunction);
    }
    boxDragFunction(drawEvent) {
        this.onBoxDrag.next(drawEvent);
    }
    boxStartFunction(drawEvent) {
        this.onBoxStart.next(drawEvent);
    }
    boxEndFunction(drawEvent) {
        this.onbBoxEnd.next(drawEvent);
    }
    changeFunction(event) {
        this.onChange.next(event);
    }
    onChangeActiveFunction(objectEvent) {
        this.onChangeActive.next(objectEvent);
    }
    onPropertyChangeFunction(objectEvent) {
        this.onPropertyChange.next(objectEvent);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ANBDragBox, deps: [{ token: 'options' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ANBDragBox }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ANBDragBox, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['options']
                }] }]; } });

class ANBDraw extends Draw {
    constructor(options) {
        super(options);
        this.options = options;
        this.onChange = new BehaviorSubject(null);
        this.onChangeActive = new BehaviorSubject(null);
        this.onDrawEnd = new BehaviorSubject(null);
        this.onDrawStart = new BehaviorSubject(null);
        this.onPropertyChange = new BehaviorSubject(null);
        this.on('drawend', this.drawEndFunction.bind(this));
        this.on('drawstart', this.drawStartFunction.bind(this));
        this.on('change', this.changeFunction.bind(this));
        this.on('change:active', this.onChangeActiveFunction.bind(this));
        this.on('propertychange', this.onPropertyChangeFunction.bind(this));
    }
    ngOnDestroy() {
        this.un('drawend', this.drawEndFunction);
        this.un('drawstart', this.drawStartFunction);
        this.un('change', this.changeFunction);
        this.un('change:active', this.onChangeActiveFunction);
        this.un('propertychange', this.onPropertyChangeFunction);
    }
    drawEndFunction(drawEvent) {
        this.onDrawEnd.next(drawEvent);
    }
    drawStartFunction(drawEvent) {
        this.onDrawStart.next(drawEvent);
    }
    changeFunction(event) {
        this.onChange.next(event);
    }
    onChangeActiveFunction(objectEvent) {
        this.onChangeActive.next(objectEvent);
    }
    onPropertyChangeFunction(objectEvent) {
        this.onPropertyChange.next(objectEvent);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ANBDraw, deps: [{ token: 'options' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ANBDraw }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ANBDraw, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['options']
                }] }]; } });

class ANBSelect extends Select {
    constructor(options) {
        super(options);
        this.options = options;
        this.onSelect = new BehaviorSubject(null);
        this.onChange = new BehaviorSubject(null);
        this.onChangeActive = new BehaviorSubject(null);
        this.onPropertyChange = new BehaviorSubject(null);
        this.on('select', this.selectFunction.bind(this));
        this.on('change', this.changeFunction.bind(this));
        this.on('change:active', this.onChangeActiveFunction.bind(this));
        this.on('propertychange', this.onPropertyChangeFunction.bind(this));
    }
    selectFunction(selectEvent) {
        this.onSelect.next(selectEvent);
    }
    changeFunction(event) {
        this.onChange.next(event);
    }
    onChangeActiveFunction(objectEvent) {
        this.onChangeActive.next(objectEvent);
    }
    onPropertyChangeFunction(objectEvent) {
        this.onPropertyChange.next(objectEvent);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ANBSelect, deps: [{ token: 'options' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ANBSelect }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ANBSelect, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['options']
                }] }]; } });

/**
 * Exposes a showLoader subject to manage the loader in the map
 *
 */
class LoaderService {
    constructor() {
        /**
         * Subscribe to this BehaviorSubject to manage the loader
         *
         * BehaviorSubject<boolean>
         * @memberof LoaderService
         */
        this.showLoader = new BehaviorSubject(false);
        this.counter = 0;
    }
    /**
     * Method to update the showloader behaviorsubject.
     * There is a timeout of 2 seconds to make sure the user sees there is something happening.
     * Calling the method multiple times, will start the timer again
     *
     * @param show Show/Hide the loader
     * @memberof LoaderService
     */
    displayLoader(show) {
        if (show) {
            this.counter++;
        }
        else if (this.counter > 0) {
            this.counter--;
        }
        const hasResult = this.counter > 0;
        if (hasResult) {
            clearTimeout(this.timeOutId);
            this.showLoader.next(hasResult);
        }
        else {
            clearTimeout(this.timeOutId);
            this.timeOutId = setTimeout(() => {
                this.showLoader.next(hasResult);
            }, 2000);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: LoaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: LoaderService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: LoaderService, decorators: [{
            type: Injectable
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Exposes methods to zoom
 *
 */
class ZoomService {
    /**
     * Zooms to the given extent on the map
     * Default animation duration is 750
     * We use number[] as extent as it could happen that a map hasn't fully loaded when zoomToExtent is called
     * If we use an Extent object, getMap() is called in getView().fit(), the map can be undefined
     *
     * @param mapId The mapId
     * @param extent Extent in coordinates, e.g. [10000.00, 9999.99, 10001.01, 10002.02]
     * @param duration Animation duration (miliseconds)
     * @memberof ZoomService
     */
    zoomToExtent(map, extent, duration = 750) {
        map.getView().fit(extent, {
            size: map.getSize(),
            duration: duration,
        });
    }
    /** Zoom with options, use if you want padding. **/
    zoomToExtentWithOptions(map, extent, options) {
        map.getView().fit(extent, options);
    }
    /**
     * Zooms to the extent depending on the default extent provided by the layerservice proj4 definitions
     * Default animation duration is 750
     *
     * @param mapId The mapId
     * @param baseMap The ANB Layer object with basemap = true
     * @param duration The animation duration
     * @memberof ZoomService
     */
    zoomToDefaultExtent(map, projection, duration = 750) {
        const olExtent = get(projection).getExtent();
        this.zoomToExtent(map, olExtent, duration);
    }
    zoomToFeatures(map, layer) {
        const olLayerSource = layer.getSource();
        const features = olLayerSource.getFeatures();
        if (!features) {
            return;
        }
        let olExtent;
        features.forEach((feature) => {
            if (!olExtent) {
                olExtent = feature.getGeometry().getExtent();
            }
            extend(olExtent, feature.getGeometry().getExtent());
        });
        if (!olExtent) {
            return;
        }
        map.getView().fit(olExtent);
    }
    zoomToFeature(map, layer, featureId) {
        const olLayerSource = layer.getSource();
        const feature = olLayerSource.getFeatureById(featureId);
        const olExtent = feature.getGeometry().getExtent();
        extend(olExtent, feature.getGeometry().getExtent());
        if (!olExtent) {
            return;
        }
        map.getView().fit(olExtent);
    }
    zoomToSearchCoordinatesFeature(map, layer) {
        this.zoomToFeature(map, layer, 'searchedcoordinates');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ZoomService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ZoomService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ZoomService, decorators: [{
            type: Injectable
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Use this component to initialize map(s).
 * To make it work, provide a unique mapId and some layers.
 * selector: anb-ol-map
 * input parameters: layers: Layer[], mapId: string
 *
 */
class OlMapComponent {
    constructor(loaderService, zoomService, configManagerService, http, elementRef) {
        this.loaderService = loaderService;
        this.zoomService = zoomService;
        this.configManagerService = configManagerService;
        this.http = http;
        this.elementRef = elementRef;
        this.projection = 'EPSG:31370';
        this.isButtonGroupInitiallyOpen = true;
        this.buttonGroupTitle = 'Acties';
        this.addActionButton = true;
        this.legendLabel = 'Legende';
        this.layerVisibilityChanged = new EventEmitter();
        this.buttonEvent = new EventEmitter();
        /** Emits the current zoom level */
        this.zoomChanged = new EventEmitter();
        /** Emits the current view extent */
        this.extentSrc = new BehaviorSubject([]);
        this.extent = this.extentSrc.asObservable();
        this.isLoading = false;
        this.layerGroupViewModel = new Array();
        this.layerGroupIcon = faLayerGroup;
        this.chevronUp = faChevronUp;
        this.chevronDown = faChevronDown;
        this.suitcase = faSuitcase;
        this.generateButtons = false;
        this.legendSidebarOpen = false;
        this.sideBarDisplay = 'none';
        this.sideBarWidth = '0%';
        this.legendButtonDisplay = 'block';
        this.buttonGroupDisplay = 'none';
        this.buttonGroupOpenButtonDisplay = 'flex';
        /**
         * Listener on click map. To activate, call startClickCoordinateListener() method first.
         *
         * @memberof OlMapComponent
         */
        this.clickCoordinate$ = new BehaviorSubject(null);
        this.select$ = new BehaviorSubject(null);
        this.LegendTypeEnum = LegendTypeEnum;
        this.subs = new Subscription();
        this.legendFoldCache = [];
        this.generateLegendSrc = new Subject();
        this.generateLegends$ = this.generateLegendSrc.asObservable();
        this.hasZoomedToDefaultSrc = new BehaviorSubject(false);
        this.hasZoomedToDefault$ = this.hasZoomedToDefaultSrc.asObservable();
        this.legendLoading = false;
        /** Generates legends based on the config or default config file. */
        this.legends$ = () => {
            this.layerGroupViewModel.length = 0;
            const config$ = this.config ?? this.configManagerService.getConfiguration(this.mapId);
            const extent$ = config$.pipe(shareReplay(1), switchMap((config) => this.getExtents(config)));
            return combineLatest([config$, extent$, this.layersLoaded$]).pipe(filter(([configuration, , loaded]) => !!configuration?.layers?.imageLayers && loaded), debounce(([configuration]) => timer(configuration?.queryLegendOptions?.debounceTime ?? 150)), tap(() => (this.legendLoading = true)), switchMap(([configuration, extent]) => {
                this.layerGroupViewModel.length = 0;
                const arcGisServiceLegends$ = this.getArcgisServiceUrls$(configuration).pipe(switchMap((groups) => {
                    if (!groups?.length) {
                        return of([]);
                    }
                    return forkJoin(groups.map(({ url, layerIds }) => {
                        const legendServicePath = isEmpty(configuration?.queryLegendOptions)
                            ? `/legend?f=pjson`
                            : this.buildQueryLegendsUrl(extent, layerIds, configuration.queryLegendOptions);
                        const fullPath = `${url}${legendServicePath}`.replace(/([^:]\/)\/+/g, '$1');
                        return this.http.get(fullPath);
                    })).pipe(distinctUntilChanged(), map((results) => {
                        let layers = [];
                        results?.forEach((x) => {
                            layers = [...layers, ...x.layers];
                        });
                        return layers;
                    }));
                }));
                const arcGisLayers$ = (layer$, arcgisLegends) => layer$.pipe(filter((layer) => layer.legendConfigOptions.type === LegendTypeEnum.ARCGIS), map((layer) => {
                    const { layerConfigOptions, legendConfigOptions } = layer;
                    const pushLegends = [];
                    const sourceLayerIds = [Number(layer.sourceConfigOptions?.params?.layers)];
                    const arcgisLegend = arcgisLegends.find((i) => sourceLayerIds.includes(i.layerId));
                    if (arcgisLegend) {
                        const legendViewModels = arcgisLegend.legend.map((l) => {
                            return { contentType: l.contentType, imageData: l.imageData, label: l.label };
                        });
                        if (!layerConfigOptions.parent) {
                            pushLegends.push({ layerConfigOptions, legendConfigOptions, legendViewModels });
                        }
                        else {
                            const { id, label } = layerConfigOptions.parent;
                            const parent = { id, label };
                            const groupName = this.layerGroupViewModel.find((c) => c.groupName === label);
                            if (!groupName) {
                                pushLegends.push({ layerConfigOptions, legendConfigOptions, legendViewModels, parent });
                            }
                            else {
                                pushLegends.push({ layerConfigOptions, legendConfigOptions, legendViewModels, parent });
                            }
                        }
                    }
                    return pushLegends;
                }));
                const wmsLayers$ = (layer$) => layer$.pipe(filter((layer) => layer?.legendConfigOptions?.type === LegendTypeEnum.WMS), map((layer) => {
                    const { layerConfigOptions, legendConfigOptions } = layer;
                    const { parent } = layerConfigOptions;
                    let legendViewModels = null;
                    if (legendConfigOptions.visible) {
                        const imageSrcUrl = this.buildWMSGetLegendGraphicURL(layer);
                        const label = legendConfigOptions.label;
                        legendViewModels = [{ imageSrcUrl, label }];
                    }
                    return [{ layerConfigOptions, legendConfigOptions, legendViewModels, parent }];
                }));
                const wmtsLayers$ = (layer$) => layer$.pipe(filter((layer) => layer?.legendConfigOptions?.type === LegendTypeEnum.WMTS), map((layer) => {
                    const { layerConfigOptions, legendConfigOptions } = layer;
                    const { parent } = layerConfigOptions;
                    const legendViewModels = null;
                    return [{ layerConfigOptions, legendConfigOptions, legendViewModels, parent }];
                }));
                const defaultLayer$ = (layer$) => layer$.pipe(filter((layer) => ![LegendTypeEnum.WMTS, LegendTypeEnum.WMS, LegendTypeEnum.ARCGIS].includes(layer?.legendConfigOptions?.type)), map(() => []));
                return arcGisServiceLegends$.pipe(switchMap((gis) => from(configuration.layers.imageLayers).pipe(mergeMap((layer) => {
                    const layer$ = of(layer);
                    return merge(arcGisLayers$(layer$, gis), wmsLayers$(layer$), wmtsLayers$(layer$), defaultLayer$(layer$));
                }), catchError((err) => {
                    console.error(err);
                    return EMPTY;
                }))));
            }), tap(() => (this.legendLoading = false)), finalize(() => (this.legendLoading = false)));
        };
    }
    ngOnChanges(changes) {
        const shouldRegenerateLegends = changes.mapId?.previousValue !== changes.mapId?.currentValue && this.generateLegend && !changes.mapId?.isFirstChange();
        if (shouldRegenerateLegends) {
            this.generateLegendSrc.next();
        }
    }
    ngOnInit() {
        if (this.isButtonGroupInitiallyOpen === true) {
            this.buttonGroupDisplay = 'flex';
            this.buttonGroupOpenButtonDisplay = 'none';
        }
        else {
            this.buttonGroupDisplay = 'none';
            this.buttonGroupOpenButtonDisplay = 'flex';
        }
        if (Array.isArray(this.mapButtons) && this.mapButtons.length) {
            this.generateButtons = true;
        }
        this.setProj4Defs();
        const loaderSub = this.loaderService.showLoader.subscribe((value) => (this.isLoading = value));
        this.subs.add(loaderSub);
        const legendsSub = this.generateLegends$.pipe(switchMap(() => this.legends$())).subscribe((legend) => legend?.forEach((layer) => {
            if (layer?.legendViewModels?.length || layer.legendConfigOptions.type !== LegendTypeEnum.ARCGIS) {
                this.pushLegends(layer.layerConfigOptions, layer.legendConfigOptions, layer.legendViewModels, layer.parent);
            }
        }));
        this.subs.add(legendsSub);
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
        this.elementRef.nativeElement.remove();
    }
    ngAfterViewInit() {
        this.map = this.initMap(this.mapId);
        this.layers = this.map.getLayers();
        this.setView(this.map, this.projection, this.zoom, this.minZoom, this.maxZoom);
        this.map.getView().on('change:resolution', (e) => {
            this.zoomChanged.emit(e.oldValue);
            const extent = this.map.getView().calculateExtent(this.map.getSize());
            this.extentSrc.next(extent);
        });
        this.map.on('moveend', () => {
            const extent = this.map.getView().calculateExtent(this.map.getSize());
            this.extentSrc.next(extent);
        });
        const zoomSub = this.layersLoaded$
            .pipe(withLatestFrom(this.hasZoomedToDefault$), filter(([loaded, hasZoomed]) => loaded && !hasZoomed), tap(() => {
            this.zoomService.zoomToDefaultExtent(this.map, this.projection, 750);
            this.setHeight();
            this.hasZoomedToDefaultSrc.next(true);
        }))
            .subscribe();
        this.subs.add(zoomSub);
        const layersSub = this.layersLoaded$
            .pipe(filter((loaded) => loaded), tap(() => {
            this.anbLayers.forEach((anbLayer) => {
                this.map.addLayer(anbLayer.layer);
            });
        }), filter(() => this.generateLegend === true), tap(() => {
            this.generateLegendSrc.next();
        }))
            .subscribe();
        this.subs.add(layersSub);
    }
    setHeight() {
        const mapTargetElement = this.map.getTargetElement();
        if (mapTargetElement.offsetHeight <= 0) {
            mapTargetElement.style.height = mapTargetElement.offsetWidth / 2 + 'px';
        }
        this.map.updateSize();
    }
    buildQueryLegendsUrl(extent, layerIds, options) {
        let url = `/queryLegends?f=pjson&size=${options.size},${options.size}&layers=show:${layerIds.join(',')}`;
        if (extent?.length) {
            url = `${url}&bbox=${extent.join(',')}`;
        }
        if (!!options?.visibleOnly) {
            url = `${url}&returnVisibleOnly=${options.visibleOnly}`;
        }
        if (!!options?.backgroundTransparent) {
            url = `${url}&&transparent=${options.backgroundTransparent}`;
        }
        return url;
    }
    getArcgisServiceUrls$(configuration) {
        const arcGisLayers = configuration.layers.imageLayers.filter((layer) => layer.legendConfigOptions.type === LegendTypeEnum.ARCGIS);
        const result = [];
        if (arcGisLayers.length > 0) {
            arcGisLayers
                .map((layer) => {
                return {
                    url: layer.sourceConfigOptions.url,
                    layerIds: Number(layer.sourceConfigOptions.params.layers),
                };
            })
                .reduce((res, value) => {
                if (!res[value.url]) {
                    res[value.url] = { url: value.url, layerIds: [] };
                    result.push(res[value.url]);
                }
                res[value.url].layerIds.push(value.layerIds);
                return res;
            }, {});
        }
        return of(result);
    }
    getExtents(config) {
        if (isEmpty(config?.queryLegendOptions)) {
            return of([]);
        }
        return config?.queryLegendOptions?.extents?.length ? of(config.queryLegendOptions.extents) : this.extent;
    }
    /**
     *  Build Url to get the legend via GetLegendGraphic for WMS layers
     * @param imageLayer;
     * @returns string;
     */
    buildWMSGetLegendGraphicURL(imageLayer) {
        const url = imageLayer.sourceConfigOptions.url;
        const layer = imageLayer.sourceConfigOptions.params['layers'];
        const version = imageLayer.sourceConfigOptions.params['version'];
        const versionrequest = version ? `&version=${version}` : '';
        return `${url}request=GetLegendGraphic&width=25&height=25&format=image/png&layer=${layer}${versionrequest}`;
    }
    /** ArcGis legends puts the correct group. */
    /** @deprecated This should NOT be called from outside the anb-ng-maps package. */
    mapArcgislegend(arcgisLegends, imageLayerConfig, legendConfigOptions, layerIds) {
        if (arcgisLegends) {
            arcgisLegends.forEach((legend) => {
                if (layerIds.includes(legend.layerId) || layerIds.length === 0) {
                    const legendViewModels = new Array();
                    legend.legend.forEach((l) => {
                        legendViewModels.push({ contentType: l.contentType, imageData: l.imageData, label: l.label });
                    });
                    if (!imageLayerConfig.parent) {
                        this.pushLegends(imageLayerConfig, legendConfigOptions, legendViewModels);
                        return;
                    }
                    const { id, label } = imageLayerConfig.parent;
                    const parent = { id, label };
                    const groupName = this.layerGroupViewModel.find((c) => c.groupName === label);
                    if (!groupName) {
                        this.pushLegends(imageLayerConfig, legendConfigOptions, legendViewModels, parent);
                        return;
                    }
                    this.pushLegends(imageLayerConfig, legendConfigOptions, legendViewModels, parent);
                }
            });
        }
    }
    /** Legends push the correct Layer arrays and sort legends. */
    pushLegends(imageLayerConfig, legendConfigOptions, legendViewModels, parent) {
        const parentName = parent?.label ? this.layerGroupViewModel.find((c) => c.groupName === parent.label) : null;
        const legendLayerViewModel = [];
        const legendArray = parentName ? parentName.legendValue : legendLayerViewModel;
        const item = this.legendFoldCache.find((f) => f.layerId === imageLayerConfig.layerId);
        if (!item) {
            const add = { layerId: imageLayerConfig.layerId, fold: true };
            this.legendFoldCache.push(add);
        }
        legendArray.push({
            layerId: imageLayerConfig.layerId,
            label: legendConfigOptions.label,
            rowIndex: legendConfigOptions.rowIndex,
            legend: legendViewModels,
            visible: imageLayerConfig.visible,
            type: legendConfigOptions.type,
            fold: item?.fold ?? true,
            parent,
            groupSingleItems: legendConfigOptions.groupSingleItems ?? true,
        });
        if (!parentName) {
            const groupName = imageLayerConfig.parent ? imageLayerConfig.parent.label : null;
            const layerModel = {
                groupName,
                legendValue: legendLayerViewModel,
                rowIndex: legendConfigOptions.rowIndex,
            };
            this.layerGroupViewModel.push(layerModel);
            this.layerGroupViewModel.sort((a, b) => a.rowIndex - b.rowIndex);
            this.layerGroupViewModel.forEach((element) => {
                element.legendValue.sort((a, b) => a.rowIndex - b.rowIndex);
            });
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onResize(event) {
        this.map.updateSize();
    }
    /**
     * Returns the layer with the specified id
     *
     * @param id The id of the Laer
     * @returns The ANB Layer object
     * @memberof LayerService
     */
    getLayerById(layer) {
        return this.anbLayers.find((c) => c.id === layer.id);
    }
    /**
     * Sets the proj4 defintion
     *
     * @memberof LayerService
     */
    setProj4Defs() {
        proj4.defs('EPSG:31370', '+proj=lcc +lat_1=51.16666723333333 +lat_2=49.8333339 +lat_0=90 +lon_0=4.367486666666666 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +towgs84=-106.869,52.2978,-103.724,0.3366,-0.457,1.8422,-1.2747 +units=m +no_defs');
        // proj4.defs('EPSG:31300', '+proj=lcc +lat_1=49.83333333333334 +lat_2=51.16666666666666 +lat_0=90 +lon_0=4.356939722222222 +x_0=150000.01256 +y_0=5400088.4378 +ellps=intl +towgs84=-106.869,52.2978,-103.724,0.3366,-0.457,1.8422,-1.2747 +units=m +no_defs');
        register(proj4);
        const epsg31370 = get('EPSG:31370');
        epsg31370.setExtent([22084.25, 153207.35550000146, 258864.9531000033, 243332.98050000146]);
    }
    /**
     * Initializes the map and stores it in the maps array property.
     * The mapId should be unique
     *
     * @param mapId The mapId. Should be unique.
     * @param defaultInteractions =null The default OpenLayers interactions (ol.interaction.defaults())
     * @memberof MapService
     */
    initMap(mapId, defaultInteractions = null) {
        if (defaultInteractions == null) {
            defaultInteractions = defaults();
        }
        const controls = defaults$1({
            attribution: false,
        });
        const currentMap = new Map({
            layers: [],
            target: document.getElementById(mapId),
            controls: controls,
            interactions: defaultInteractions,
        });
        currentMap.set('id', mapId);
        currentMap.set('defaultInteractions', defaultInteractions);
        if (this.generateLegend) {
            this.setControls(currentMap, mapId);
        }
        if (this.generateButtons) {
            this.setButtonGroupControl(currentMap, mapId);
        }
        return currentMap;
    }
    /*
     * Add legend sidebar and legend button to map
     *
     */
    setControls(theMap, mapId) {
        const legendSideBarElement = document.getElementById('legendSideBar_' + mapId);
        const legendButtonElement = document.getElementById('legendButton_' + mapId);
        const legendButtonControl = new Control({
            element: legendButtonElement,
        });
        const legendSideBarControl = new Control({
            element: legendSideBarElement,
            target: theMap.getTargetElement().className,
        });
        theMap.addControl(legendSideBarControl);
        theMap.addControl(legendButtonControl);
    }
    setButtonGroupControl(theMap, mapId) {
        const legendButtonsModelElement = document.getElementById('buttonGroupModel_' + mapId);
        const legendButtonsModelControl = new Control({
            element: legendButtonsModelElement,
        });
        theMap.addControl(legendButtonsModelControl);
    }
    /**
     * Sets the view of the map depending on the layers' extent from capabilities service.
     * Usually, applicable on baselayers
     *
     * @param mapId The mapId
     * @param layer The ANB Layer object
     * @memberof LayerService
     */
    setView(theMap, projection, zoom, minZoom, maxZoom) {
        const center = [0, 0];
        const view = new View({ projection, center, zoom, minZoom, maxZoom });
        theMap.setView(view);
    }
    /**
     * When the map is in a hidden component (toggle, wizard, ...), the rendere has problems to show the map.
     * This is a fix to show the map in full with (layout--wide)
     * @param mapId The mapid
     * @param elementRef Angular ElementRef
     * @param renderer Angular Renderer2
     */
    hiddenMapFix(theMap, elementRef, renderer) {
        const promise = new Promise((resolve) => {
            const olMap = theMap;
            const layoutWide = document.getElementsByClassName('layout--wide');
            const computedStyle = getComputedStyle(layoutWide[0]);
            const el = elementRef.nativeElement.querySelector('.ol-map');
            const computedWidth = computedStyle.width.replace(/\D/g, '');
            const width = parseFloat(computedWidth);
            const height = width / 2;
            renderer.setStyle(el, 'width', width + 'px');
            renderer.setStyle(el, 'height', height + 'px');
            setTimeout(() => {
                olMap.updateSize();
                renderer.removeStyle(el, 'width');
                renderer.removeStyle(el, 'height');
                setTimeout(() => {
                    olMap.updateSize();
                    resolve(true);
                }, 100);
            }, 100);
        });
        return promise;
    }
    /**
     * Adds interaction to the current map instance. Use ANB interaction to be able to use events as behavioursubject.
     * i.e.: use ANBDraw instead of Draw.
     *
     * Interaction interaction
     * @memberof OlMapComponent
     */
    addInteraction(interaction) {
        this.map.addInteraction(interaction);
    }
    removeInteraction(interaction) {
        this.map.removeInteraction(interaction);
    }
    onChangeLegendVisibility(legend) {
        const layers = this.map.getLayers().getArray();
        const baseLayer = layers.find((f) => f.get('id') === legend.layerId);
        if (baseLayer) {
            baseLayer.setVisible(!baseLayer.getVisible());
        }
        this.layerVisibilityChanged.emit(baseLayer);
    }
    /**
     * Returns the layer visibility in the legend sidebar
     *
     * @memberof LegendSidebarComponent
     */
    getLayerVisibility(legend) {
        const layer = this.getLayerByLayerId(legend.layerId);
        if (!layer) {
            return false;
        }
        return layer.getVisible();
    }
    fold(legend) {
        legend.fold = !legend.fold;
        const item = this.legendFoldCache.find((f) => f.layerId === legend.layerId);
        item.fold = legend.fold;
    }
    /**
     * Returns the layer with the specified id
     *
     * @memberof LegendSidebarComponent
     */
    getLayerByLayerId(layerId) {
        if (!this.layers) {
            return null;
        }
        const layers = this.layers.getArray();
        const layer = layers.find((x) => x.getProperties()['id'] === layerId);
        if (!layer) {
            return null;
        }
        return layer;
    }
    /**
     * Closes legend-sidebar
     *
     * @memberof LegendSidebarComponent
     */
    closeNav() {
        this.sideBarDisplay = 'none';
        this.sideBarWidth = '0%';
        this.legendButtonDisplay = 'block';
    }
    /**
     * Opens legend-sidebar
     *
     * @memberof LegendButtonComponent
     */
    openNav() {
        this.sideBarDisplay = 'block';
        this.sideBarWidth = '30%';
        this.legendButtonDisplay = 'none';
    }
    closeButtonGroup() {
        this.buttonGroupDisplay = 'none';
        this.buttonGroupOpenButtonDisplay = 'flex';
        this.sendEvent('closeButtonGroup');
    }
    openButtonGroup() {
        this.buttonGroupDisplay = 'flex';
        this.buttonGroupOpenButtonDisplay = 'none';
        this.sendEvent('openButtonGroup');
    }
    sendEvent(id) {
        this.buttonEvent.emit(id);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: OlMapComponent, deps: [{ token: LoaderService }, { token: ZoomService }, { token: i3.ConfigManagerService }, { token: i4.HttpClient }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.7", type: OlMapComponent, selector: "anb-ol-map", inputs: { mapId: "mapId", anbLayers: "anbLayers", zoom: "zoom", minZoom: "minZoom", maxZoom: "maxZoom", projection: "projection", layersLoaded$: "layersLoaded$", customControls: "customControls", generateLegend: "generateLegend", mapButtons: "mapButtons", isButtonGroupInitiallyOpen: "isButtonGroupInitiallyOpen", buttonGroupTitle: "buttonGroupTitle", config: "config", addActionButton: "addActionButton", legendLabel: "legendLabel" }, outputs: { layerVisibilityChanged: "layerVisibilityChanged", buttonEvent: "buttonEvent", zoomChanged: "zoomChanged", extent: "extent" }, host: { listeners: { "window:resize": "onResize($event)" } }, usesOnChanges: true, ngImport: i0, template: "<div *ngIf=\"generateLegend\">\n  <div class=\"ol-unselectable legendOpenButton\" [id]=\"'legendButton_' + mapId\" [ngStyle]=\"{ display: legendButtonDisplay }\">\n    <button (click)=\"openNav()\">\n      {{ legendLabel }}\n      <fa-icon [icon]=\"chevronDown\" [classes]=\"['icon-class']\"></fa-icon>\n    </button>\n  </div>\n  <div class=\"legendbar ol-unselectable\" [id]=\"'legendSideBar_' + mapId\" [ngStyle]=\"{ display: sideBarDisplay, width: sideBarWidth }\">\n    <button class=\"legendCloseButton\" (click)=\"closeNav()\">\n      <div class=\"vl-grid closeButton\">\n        <div class=\"vl-col--12-12 vl-u-align-right\">\n          {{ legendLabel }}\n          <fa-icon [icon]=\"chevronUp\" [classes]=\"['icon-class']\"></fa-icon>\n        </div>\n      </div>\n    </button>\n    <div class=\"legendContent\" *ngIf=\"legendLoading\">\n      <div class=\"vl-col--12-12 labelPosition\">\n        <div class=\"loader loader-align\"></div>\n      </div>\n    </div>\n    <div class=\"legendContent\" *ngIf=\"!legendLoading\">\n      <div class=\"vl-grid vl-u-text--small\">\n        <div class=\"vl-col--12-12 labelPosition\" *ngFor=\"let layer of layerGroupViewModel\">\n          <label\n            ><strong>{{ layer.groupName }}</strong></label\n          >\n          <div class=\"vl-grid\" *ngFor=\"let legend of layer.legendValue\" [ngClass]=\"{ subLayerPosition: layer.groupName }\">\n            <ng-container\n              *ngIf=\"\n                (legend.legend?.length <= 1 && legend.type !== LegendTypeEnum.WMS && legend.groupSingleItems) || !legend.legend;\n                else labelExist\n              \"\n            >\n              <div class=\"vl-col--8-12\">\n                <label>\n                  <input\n                    type=\"checkbox\"\n                    [value]=\"getLayerVisibility(legend)\"\n                    [checked]=\"getLayerVisibility(legend)\"\n                    (change)=\"onChangeLegendVisibility(legend)\"\n                  />\n                  {{ legend.label }}\n                </label>\n              </div>\n              <div class=\"vl-col--3-12 vl-u-align-right\" *ngFor=\"let item of legend.legend\">\n                <span>\n                  <img [src]=\"'data:' + item.contentType + ';base64,' + item.imageData\" />\n                </span>\n              </div>\n              <div class=\"vl-col--12-12\">\n                <hr class=\"legendSidebarHr\" />\n              </div>\n            </ng-container>\n            <ng-template #labelExist>\n              <ng-container *ngIf=\"legend.legend?.length <= 2 && legend.type !== LegendTypeEnum.WMS; else accordion\">\n                <div class=\"vl-col--12-12\">\n                  <label>\n                    <input\n                      type=\"checkbox\"\n                      [value]=\"getLayerVisibility(legend)\"\n                      [checked]=\"getLayerVisibility(legend)\"\n                      (change)=\"onChangeLegendVisibility(legend)\"\n                    />\n                    {{ legend.label }}\n                  </label>\n                </div>\n                <ng-container *ngFor=\"let item of legend.legend\">\n                  <div class=\"vl-col--8-12 x\">\n                    <label>{{ item.label }}</label>\n                  </div>\n                  <div class=\"vl-col--3-12 vl-u-align-right\">\n                    <span>\n                      <img [src]=\"'data:' + item.contentType + ';base64,' + item.imageData\" />\n                    </span>\n                  </div>\n                </ng-container>\n              </ng-container>\n              <ng-template #accordion>\n                <div class=\"vl-col--8-12\">\n                  <label>\n                    <input\n                      type=\"checkbox\"\n                      [value]=\"getLayerVisibility(legend)\"\n                      [checked]=\"getLayerVisibility(legend)\"\n                      (change)=\"onChangeLegendVisibility(legend)\"\n                    />\n                    {{ legend.label }}\n                  </label>\n                </div>\n                <div class=\"vl-col--3-12 arrowPosition\">\n                  <button (click)=\"fold(legend)\" class=\"noBorder\">\n                    <fa-icon [icon]=\"chevronDown\" class=\"arrowColor\"></fa-icon>\n                  </button>\n                </div>\n                <ng-container *ngIf=\"!legend.fold == false\">\n                  <ng-container *ngFor=\"let item of legend.legend\">\n                    <div *ngIf=\"legend.type !== LegendTypeEnum.WMS\" class=\"vl-col--8-12 labelName\">\n                      <label>{{ item.label }}</label>\n                    </div>\n                    <div [ngClass]=\"legend.type !== LegendTypeEnum.WMS ? 'vl-col-3-12 vl-u-align-right' : 'vl-col--11-12 '\">\n                      <span *ngIf=\"item.contentType\">\n                        <img [src]=\"'data:' + item.contentType + ';base64,' + item.imageData\" />\n                      </span>\n                      <span *ngIf=\"!item.contentType\">\n                        <img [src]=\"item.imageSrcUrl\" />\n                      </span>\n                    </div>\n                  </ng-container>\n                </ng-container>\n              </ng-template>\n              <div class=\"vl-col--12-12\">\n                <hr class=\"legendSidebarHr\" />\n              </div>\n            </ng-template>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div class=\"buttonGroupModel\" [id]=\"'buttonGroupModel_' + mapId\">\n  <div\n    *ngIf=\"addActionButton\"\n    class=\"buttonGroupOpenButton\"\n    (click)=\"openButtonGroup()\"\n    [ngStyle]=\"{ display: buttonGroupOpenButtonDisplay }\"\n  >\n    <button>\n      <fa-icon [icon]=\"suitcase\"></fa-icon>\n    </button>\n  </div>\n  <div *ngIf=\"addActionButton\" class=\"buttonGroup\" [ngStyle]=\"{ display: buttonGroupDisplay }\">\n    <button class=\"actiesButton\" (click)=\"closeButtonGroup()\">\n      {{ buttonGroupTitle }}\n      <fa-icon [icon]=\"chevronUp\" [classes]=\"['icon-class']\"></fa-icon>\n    </button>\n    <ng-container *ngFor=\"let button of mapButtons\">\n      <button\n        (click)=\"sendEvent(button.id)\"\n        class=\"genericButtons\"\n        [ngClass]=\"{ buttonDisabled: button.status == 'disabled', buttonActive: button.status == 'active' }\"\n      >\n        <img *ngIf=\"button.imageData\" [src]=\"'data:' + button.imageContentType + ';base64,' + button.imageData\" />\n        <span *ngIf=\"button.title\">{{ button.title }}</span>\n      </button>\n    </ng-container>\n  </div>\n</div>\n<div [attr.id]=\"mapId\" class=\"ol-map\">\n  <div *ngIf=\"isLoading\" class=\"ol-map-loader\"><span class=\"u-visually-hidden\">Aan het laden</span></div>\n  <ng-content></ng-content>\n</div>\n", styles: [":host{height:100%;width:100%}.ol-map-loader{z-index:9999;position:absolute;right:40px}.legendPosition{position:absolute;top:0;right:0}.labelPosition{padding-left:15%}.labelPosition .loader{margin-top:2rem;margin-left:35%}.subLayerPosition{padding-left:3.5%}.legendImages{margin-left:.4%;display:inline-block}.legendSideBarBtn,.legendPosition,.legendSideBarBtn:hover,.legendPosition:hover{background-color:#333}.legendSidebarHr{border:.7px solid #ffffff}.labelName{padding-left:20%;font-style:italic;font-size:small}.noBorder{border:0px;background-color:transparent!important;margin-right:0!important}.noBorder:focus{outline:none}.subLayerPosition img{max-width:none}.legendOpenButton{position:absolute;top:1rem;right:1rem;background-color:#fff6;padding:2px;border-radius:4px}.legendOpenButton button{display:block;margin:1px;padding:2 0 2 0;color:#fff!important;font-size:1em;font-weight:400;text-decoration:none;text-align:center;height:2em;line-height:.4em;background-color:#003c8880;border:none;border-radius:2px}.legendbar{height:80%;position:absolute;z-index:999;right:1rem;background-color:#f5f5f5;overflow-x:hidden;overflow-y:hidden;top:1rem;display:none;max-height:80%;padding:3px;border-radius:4px;pointer-events:auto}.legendCloseButton{display:block;color:#fff!important;font-size:1em;font-weight:400;text-decoration:none;text-align:center;height:2em;line-height:.4em;background-color:#003c8880;border:none;border-radius:2px;width:100%}.legendContent{width:100%;display:block;height:92%;background-color:#f5f5f5;overflow-y:auto;overflow-x:auto;padding-top:1rem;position:relative;border-radius:2px;box-sizing:border-box}.arrowColor{color:#333}.arrowPosition{display:flex;justify-content:flex-end}.closeButton{width:100%;margin-left:0}.icon-class{padding-top:2px}.buttonGroupModel{position:absolute;top:80px;left:8px}.buttonGroup button{margin:1px;color:#fff;font-size:1.1em;font-weight:400;text-decoration:none;text-align:left;border:none;border-radius:2px}.buttonGroup{display:flex;background-color:#fff6;padding:2px;border-radius:4px;flex-direction:column;text-align:left;color:#fff}.actiesButton{font-weight:600!important;color:#003c8880!important}.genericButtons{display:flex;flex-direction:row;justify-content:flex-start;align-items:center;align-content:center;gap:.5em;padding:3px 6px;background-color:#003c8880}.genericButtons img{width:20px;height:20px}.buttonActive{border:5px solid white!important}.buttonDisabled{background-color:gray!important;pointer-events:none;cursor:not-allowed}.buttonGroupOpenButton{display:flex;background-color:#fff6;padding:2px;border-radius:4px;flex-direction:column;text-align:center;color:#fff}.buttonGroupOpenButton button{margin:1px;color:#fff;font-size:1.14em;font-weight:400;text-decoration:none;text-align:left;border:none;border-radius:2px;background-color:#003c8880;width:1.375em;height:1.375em;line-height:1.375em!important}.fa-suitcase{font-size:.75em;font-weight:500}\n", ".ol-box{box-sizing:border-box;border-radius:2px;border:1.5px solid rgb(179,197,219);background-color:#fff6}.ol-mouse-position{top:8px;right:8px;position:absolute}.ol-scale-line{background:rgba(0,60,136,.3);border-radius:4px;bottom:8px;left:8px;padding:2px;position:absolute}.ol-scale-line-inner{border:1px solid #eee;border-top:none;color:#eee;font-size:10px;text-align:center;margin:1px;will-change:contents,width;transition:all .25s}.ol-scale-bar{position:absolute;bottom:8px;left:8px}.ol-scale-step-marker{width:1px;height:15px;background-color:#000;float:right;z-Index:10}.ol-scale-step-text{position:absolute;bottom:-5px;font-size:12px;z-Index:11;color:#000;text-shadow:-2px 0 #FFFFFF,0 2px #FFFFFF,2px 0 #FFFFFF,0 -2px #FFFFFF}.ol-scale-text{position:absolute;font-size:14px;text-align:center;bottom:25px;color:#000;text-shadow:-2px 0 #FFFFFF,0 2px #FFFFFF,2px 0 #FFFFFF,0 -2px #FFFFFF}.ol-scale-singlebar{position:relative;height:10px;z-Index:9;box-sizing:border-box;border:1px solid black}.ol-unsupported{display:none}.ol-viewport,.ol-unselectable{-webkit-touch-callout:none;-webkit-user-select:none;user-select:none;-webkit-tap-highlight-color:rgba(0,0,0,0)}.ol-selectable{-webkit-touch-callout:default;-webkit-user-select:text;user-select:text}.ol-grabbing{cursor:grabbing}.ol-grab{cursor:move;cursor:grab}.ol-control{position:absolute;background-color:#fff6;border-radius:4px;padding:2px}.ol-control:hover{background-color:#fff9}.ol-zoom{top:.5em;left:.5em}.ol-rotate{top:.5em;right:.5em;transition:opacity .25s linear,visibility 0s linear}.ol-rotate.ol-hidden{opacity:0;visibility:hidden;transition:opacity .25s linear,visibility 0s linear .25s}.ol-zoom-extent{top:4.643em;left:.5em}.ol-full-screen{right:.5em;top:.5em}.ol-control button{display:block;margin:1px;padding:0;color:#fff;font-weight:700;text-decoration:none;font-size:inherit;text-align:center;height:1.375em;width:1.375em;line-height:.4em;background-color:#003c8880;border:none;border-radius:2px}.ol-control button::-moz-focus-inner{border:none;padding:0}.ol-zoom-extent button{line-height:1.4em}.ol-compass{display:block;font-weight:400;font-size:1.2em;will-change:transform}.ol-touch .ol-control button{font-size:1.5em}.ol-touch .ol-zoom-extent{top:5.5em}.ol-control button:hover,.ol-control button:focus{text-decoration:none;background-color:#003c88b3}.ol-zoom .ol-zoom-in{border-radius:2px 2px 0 0}.ol-zoom .ol-zoom-out{border-radius:0 0 2px 2px}.ol-attribution{text-align:right;bottom:.5em;right:.5em;max-width:calc(100% - 1.3em);display:flex;flex-flow:row-reverse;align-items:center}.ol-attribution a{color:#003c88b3;text-decoration:none}.ol-attribution ul{margin:0;padding:1px .5em;color:#000;text-shadow:0 0 2px #fff;font-size:12px}.ol-attribution li{display:inline;list-style:none}.ol-attribution li:not(:last-child):after{content:\" \"}.ol-attribution img{max-height:2em;max-width:inherit;vertical-align:middle}.ol-attribution button{flex-shrink:0}.ol-attribution.ol-collapsed ul{display:none}.ol-attribution:not(.ol-collapsed){background:rgba(255,255,255,.8)}.ol-attribution.ol-uncollapsible{bottom:0;right:0;border-radius:4px 0 0}.ol-attribution.ol-uncollapsible img{margin-top:-.2em;max-height:1.6em}.ol-attribution.ol-uncollapsible button{display:none}.ol-zoomslider{top:4.5em;left:.5em;height:200px}.ol-zoomslider button{position:relative;height:10px}.ol-touch .ol-zoomslider{top:5.5em}.ol-overviewmap{left:.5em;bottom:.5em}.ol-overviewmap.ol-uncollapsible{bottom:0;left:0;border-radius:0 4px 0 0}.ol-overviewmap .ol-overviewmap-map,.ol-overviewmap button{display:block}.ol-overviewmap .ol-overviewmap-map{border:1px solid #7b98bc;height:150px;margin:2px;width:150px}.ol-overviewmap:not(.ol-collapsed) button{bottom:2px;left:2px;position:absolute}.ol-overviewmap.ol-collapsed .ol-overviewmap-map,.ol-overviewmap.ol-uncollapsible button{display:none}.ol-overviewmap:not(.ol-collapsed){background:rgba(255,255,255,.8)}.ol-overviewmap-box{border:2px dotted rgba(0,60,136,.7)}.ol-overviewmap .ol-overviewmap-box:hover{cursor:move}\n"], dependencies: [{ kind: "directive", type: i5.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i5.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i5.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i5.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i6.FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "spin", "pulse", "mask", "styles", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "classes", "transform", "a11yRole"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: OlMapComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-ol-map', encapsulation: ViewEncapsulation.None, template: "<div *ngIf=\"generateLegend\">\n  <div class=\"ol-unselectable legendOpenButton\" [id]=\"'legendButton_' + mapId\" [ngStyle]=\"{ display: legendButtonDisplay }\">\n    <button (click)=\"openNav()\">\n      {{ legendLabel }}\n      <fa-icon [icon]=\"chevronDown\" [classes]=\"['icon-class']\"></fa-icon>\n    </button>\n  </div>\n  <div class=\"legendbar ol-unselectable\" [id]=\"'legendSideBar_' + mapId\" [ngStyle]=\"{ display: sideBarDisplay, width: sideBarWidth }\">\n    <button class=\"legendCloseButton\" (click)=\"closeNav()\">\n      <div class=\"vl-grid closeButton\">\n        <div class=\"vl-col--12-12 vl-u-align-right\">\n          {{ legendLabel }}\n          <fa-icon [icon]=\"chevronUp\" [classes]=\"['icon-class']\"></fa-icon>\n        </div>\n      </div>\n    </button>\n    <div class=\"legendContent\" *ngIf=\"legendLoading\">\n      <div class=\"vl-col--12-12 labelPosition\">\n        <div class=\"loader loader-align\"></div>\n      </div>\n    </div>\n    <div class=\"legendContent\" *ngIf=\"!legendLoading\">\n      <div class=\"vl-grid vl-u-text--small\">\n        <div class=\"vl-col--12-12 labelPosition\" *ngFor=\"let layer of layerGroupViewModel\">\n          <label\n            ><strong>{{ layer.groupName }}</strong></label\n          >\n          <div class=\"vl-grid\" *ngFor=\"let legend of layer.legendValue\" [ngClass]=\"{ subLayerPosition: layer.groupName }\">\n            <ng-container\n              *ngIf=\"\n                (legend.legend?.length <= 1 && legend.type !== LegendTypeEnum.WMS && legend.groupSingleItems) || !legend.legend;\n                else labelExist\n              \"\n            >\n              <div class=\"vl-col--8-12\">\n                <label>\n                  <input\n                    type=\"checkbox\"\n                    [value]=\"getLayerVisibility(legend)\"\n                    [checked]=\"getLayerVisibility(legend)\"\n                    (change)=\"onChangeLegendVisibility(legend)\"\n                  />\n                  {{ legend.label }}\n                </label>\n              </div>\n              <div class=\"vl-col--3-12 vl-u-align-right\" *ngFor=\"let item of legend.legend\">\n                <span>\n                  <img [src]=\"'data:' + item.contentType + ';base64,' + item.imageData\" />\n                </span>\n              </div>\n              <div class=\"vl-col--12-12\">\n                <hr class=\"legendSidebarHr\" />\n              </div>\n            </ng-container>\n            <ng-template #labelExist>\n              <ng-container *ngIf=\"legend.legend?.length <= 2 && legend.type !== LegendTypeEnum.WMS; else accordion\">\n                <div class=\"vl-col--12-12\">\n                  <label>\n                    <input\n                      type=\"checkbox\"\n                      [value]=\"getLayerVisibility(legend)\"\n                      [checked]=\"getLayerVisibility(legend)\"\n                      (change)=\"onChangeLegendVisibility(legend)\"\n                    />\n                    {{ legend.label }}\n                  </label>\n                </div>\n                <ng-container *ngFor=\"let item of legend.legend\">\n                  <div class=\"vl-col--8-12 x\">\n                    <label>{{ item.label }}</label>\n                  </div>\n                  <div class=\"vl-col--3-12 vl-u-align-right\">\n                    <span>\n                      <img [src]=\"'data:' + item.contentType + ';base64,' + item.imageData\" />\n                    </span>\n                  </div>\n                </ng-container>\n              </ng-container>\n              <ng-template #accordion>\n                <div class=\"vl-col--8-12\">\n                  <label>\n                    <input\n                      type=\"checkbox\"\n                      [value]=\"getLayerVisibility(legend)\"\n                      [checked]=\"getLayerVisibility(legend)\"\n                      (change)=\"onChangeLegendVisibility(legend)\"\n                    />\n                    {{ legend.label }}\n                  </label>\n                </div>\n                <div class=\"vl-col--3-12 arrowPosition\">\n                  <button (click)=\"fold(legend)\" class=\"noBorder\">\n                    <fa-icon [icon]=\"chevronDown\" class=\"arrowColor\"></fa-icon>\n                  </button>\n                </div>\n                <ng-container *ngIf=\"!legend.fold == false\">\n                  <ng-container *ngFor=\"let item of legend.legend\">\n                    <div *ngIf=\"legend.type !== LegendTypeEnum.WMS\" class=\"vl-col--8-12 labelName\">\n                      <label>{{ item.label }}</label>\n                    </div>\n                    <div [ngClass]=\"legend.type !== LegendTypeEnum.WMS ? 'vl-col-3-12 vl-u-align-right' : 'vl-col--11-12 '\">\n                      <span *ngIf=\"item.contentType\">\n                        <img [src]=\"'data:' + item.contentType + ';base64,' + item.imageData\" />\n                      </span>\n                      <span *ngIf=\"!item.contentType\">\n                        <img [src]=\"item.imageSrcUrl\" />\n                      </span>\n                    </div>\n                  </ng-container>\n                </ng-container>\n              </ng-template>\n              <div class=\"vl-col--12-12\">\n                <hr class=\"legendSidebarHr\" />\n              </div>\n            </ng-template>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div class=\"buttonGroupModel\" [id]=\"'buttonGroupModel_' + mapId\">\n  <div\n    *ngIf=\"addActionButton\"\n    class=\"buttonGroupOpenButton\"\n    (click)=\"openButtonGroup()\"\n    [ngStyle]=\"{ display: buttonGroupOpenButtonDisplay }\"\n  >\n    <button>\n      <fa-icon [icon]=\"suitcase\"></fa-icon>\n    </button>\n  </div>\n  <div *ngIf=\"addActionButton\" class=\"buttonGroup\" [ngStyle]=\"{ display: buttonGroupDisplay }\">\n    <button class=\"actiesButton\" (click)=\"closeButtonGroup()\">\n      {{ buttonGroupTitle }}\n      <fa-icon [icon]=\"chevronUp\" [classes]=\"['icon-class']\"></fa-icon>\n    </button>\n    <ng-container *ngFor=\"let button of mapButtons\">\n      <button\n        (click)=\"sendEvent(button.id)\"\n        class=\"genericButtons\"\n        [ngClass]=\"{ buttonDisabled: button.status == 'disabled', buttonActive: button.status == 'active' }\"\n      >\n        <img *ngIf=\"button.imageData\" [src]=\"'data:' + button.imageContentType + ';base64,' + button.imageData\" />\n        <span *ngIf=\"button.title\">{{ button.title }}</span>\n      </button>\n    </ng-container>\n  </div>\n</div>\n<div [attr.id]=\"mapId\" class=\"ol-map\">\n  <div *ngIf=\"isLoading\" class=\"ol-map-loader\"><span class=\"u-visually-hidden\">Aan het laden</span></div>\n  <ng-content></ng-content>\n</div>\n", styles: [":host{height:100%;width:100%}.ol-map-loader{z-index:9999;position:absolute;right:40px}.legendPosition{position:absolute;top:0;right:0}.labelPosition{padding-left:15%}.labelPosition .loader{margin-top:2rem;margin-left:35%}.subLayerPosition{padding-left:3.5%}.legendImages{margin-left:.4%;display:inline-block}.legendSideBarBtn,.legendPosition,.legendSideBarBtn:hover,.legendPosition:hover{background-color:#333}.legendSidebarHr{border:.7px solid #ffffff}.labelName{padding-left:20%;font-style:italic;font-size:small}.noBorder{border:0px;background-color:transparent!important;margin-right:0!important}.noBorder:focus{outline:none}.subLayerPosition img{max-width:none}.legendOpenButton{position:absolute;top:1rem;right:1rem;background-color:#fff6;padding:2px;border-radius:4px}.legendOpenButton button{display:block;margin:1px;padding:2 0 2 0;color:#fff!important;font-size:1em;font-weight:400;text-decoration:none;text-align:center;height:2em;line-height:.4em;background-color:#003c8880;border:none;border-radius:2px}.legendbar{height:80%;position:absolute;z-index:999;right:1rem;background-color:#f5f5f5;overflow-x:hidden;overflow-y:hidden;top:1rem;display:none;max-height:80%;padding:3px;border-radius:4px;pointer-events:auto}.legendCloseButton{display:block;color:#fff!important;font-size:1em;font-weight:400;text-decoration:none;text-align:center;height:2em;line-height:.4em;background-color:#003c8880;border:none;border-radius:2px;width:100%}.legendContent{width:100%;display:block;height:92%;background-color:#f5f5f5;overflow-y:auto;overflow-x:auto;padding-top:1rem;position:relative;border-radius:2px;box-sizing:border-box}.arrowColor{color:#333}.arrowPosition{display:flex;justify-content:flex-end}.closeButton{width:100%;margin-left:0}.icon-class{padding-top:2px}.buttonGroupModel{position:absolute;top:80px;left:8px}.buttonGroup button{margin:1px;color:#fff;font-size:1.1em;font-weight:400;text-decoration:none;text-align:left;border:none;border-radius:2px}.buttonGroup{display:flex;background-color:#fff6;padding:2px;border-radius:4px;flex-direction:column;text-align:left;color:#fff}.actiesButton{font-weight:600!important;color:#003c8880!important}.genericButtons{display:flex;flex-direction:row;justify-content:flex-start;align-items:center;align-content:center;gap:.5em;padding:3px 6px;background-color:#003c8880}.genericButtons img{width:20px;height:20px}.buttonActive{border:5px solid white!important}.buttonDisabled{background-color:gray!important;pointer-events:none;cursor:not-allowed}.buttonGroupOpenButton{display:flex;background-color:#fff6;padding:2px;border-radius:4px;flex-direction:column;text-align:center;color:#fff}.buttonGroupOpenButton button{margin:1px;color:#fff;font-size:1.14em;font-weight:400;text-decoration:none;text-align:left;border:none;border-radius:2px;background-color:#003c8880;width:1.375em;height:1.375em;line-height:1.375em!important}.fa-suitcase{font-size:.75em;font-weight:500}\n", ".ol-box{box-sizing:border-box;border-radius:2px;border:1.5px solid rgb(179,197,219);background-color:#fff6}.ol-mouse-position{top:8px;right:8px;position:absolute}.ol-scale-line{background:rgba(0,60,136,.3);border-radius:4px;bottom:8px;left:8px;padding:2px;position:absolute}.ol-scale-line-inner{border:1px solid #eee;border-top:none;color:#eee;font-size:10px;text-align:center;margin:1px;will-change:contents,width;transition:all .25s}.ol-scale-bar{position:absolute;bottom:8px;left:8px}.ol-scale-step-marker{width:1px;height:15px;background-color:#000;float:right;z-Index:10}.ol-scale-step-text{position:absolute;bottom:-5px;font-size:12px;z-Index:11;color:#000;text-shadow:-2px 0 #FFFFFF,0 2px #FFFFFF,2px 0 #FFFFFF,0 -2px #FFFFFF}.ol-scale-text{position:absolute;font-size:14px;text-align:center;bottom:25px;color:#000;text-shadow:-2px 0 #FFFFFF,0 2px #FFFFFF,2px 0 #FFFFFF,0 -2px #FFFFFF}.ol-scale-singlebar{position:relative;height:10px;z-Index:9;box-sizing:border-box;border:1px solid black}.ol-unsupported{display:none}.ol-viewport,.ol-unselectable{-webkit-touch-callout:none;-webkit-user-select:none;user-select:none;-webkit-tap-highlight-color:rgba(0,0,0,0)}.ol-selectable{-webkit-touch-callout:default;-webkit-user-select:text;user-select:text}.ol-grabbing{cursor:grabbing}.ol-grab{cursor:move;cursor:grab}.ol-control{position:absolute;background-color:#fff6;border-radius:4px;padding:2px}.ol-control:hover{background-color:#fff9}.ol-zoom{top:.5em;left:.5em}.ol-rotate{top:.5em;right:.5em;transition:opacity .25s linear,visibility 0s linear}.ol-rotate.ol-hidden{opacity:0;visibility:hidden;transition:opacity .25s linear,visibility 0s linear .25s}.ol-zoom-extent{top:4.643em;left:.5em}.ol-full-screen{right:.5em;top:.5em}.ol-control button{display:block;margin:1px;padding:0;color:#fff;font-weight:700;text-decoration:none;font-size:inherit;text-align:center;height:1.375em;width:1.375em;line-height:.4em;background-color:#003c8880;border:none;border-radius:2px}.ol-control button::-moz-focus-inner{border:none;padding:0}.ol-zoom-extent button{line-height:1.4em}.ol-compass{display:block;font-weight:400;font-size:1.2em;will-change:transform}.ol-touch .ol-control button{font-size:1.5em}.ol-touch .ol-zoom-extent{top:5.5em}.ol-control button:hover,.ol-control button:focus{text-decoration:none;background-color:#003c88b3}.ol-zoom .ol-zoom-in{border-radius:2px 2px 0 0}.ol-zoom .ol-zoom-out{border-radius:0 0 2px 2px}.ol-attribution{text-align:right;bottom:.5em;right:.5em;max-width:calc(100% - 1.3em);display:flex;flex-flow:row-reverse;align-items:center}.ol-attribution a{color:#003c88b3;text-decoration:none}.ol-attribution ul{margin:0;padding:1px .5em;color:#000;text-shadow:0 0 2px #fff;font-size:12px}.ol-attribution li{display:inline;list-style:none}.ol-attribution li:not(:last-child):after{content:\" \"}.ol-attribution img{max-height:2em;max-width:inherit;vertical-align:middle}.ol-attribution button{flex-shrink:0}.ol-attribution.ol-collapsed ul{display:none}.ol-attribution:not(.ol-collapsed){background:rgba(255,255,255,.8)}.ol-attribution.ol-uncollapsible{bottom:0;right:0;border-radius:4px 0 0}.ol-attribution.ol-uncollapsible img{margin-top:-.2em;max-height:1.6em}.ol-attribution.ol-uncollapsible button{display:none}.ol-zoomslider{top:4.5em;left:.5em;height:200px}.ol-zoomslider button{position:relative;height:10px}.ol-touch .ol-zoomslider{top:5.5em}.ol-overviewmap{left:.5em;bottom:.5em}.ol-overviewmap.ol-uncollapsible{bottom:0;left:0;border-radius:0 4px 0 0}.ol-overviewmap .ol-overviewmap-map,.ol-overviewmap button{display:block}.ol-overviewmap .ol-overviewmap-map{border:1px solid #7b98bc;height:150px;margin:2px;width:150px}.ol-overviewmap:not(.ol-collapsed) button{bottom:2px;left:2px;position:absolute}.ol-overviewmap.ol-collapsed .ol-overviewmap-map,.ol-overviewmap.ol-uncollapsible button{display:none}.ol-overviewmap:not(.ol-collapsed){background:rgba(255,255,255,.8)}.ol-overviewmap-box{border:2px dotted rgba(0,60,136,.7)}.ol-overviewmap .ol-overviewmap-box:hover{cursor:move}\n"] }]
        }], ctorParameters: function () { return [{ type: LoaderService }, { type: ZoomService }, { type: i3.ConfigManagerService }, { type: i4.HttpClient }, { type: i0.ElementRef }]; }, propDecorators: { mapId: [{
                type: Input
            }], anbLayers: [{
                type: Input
            }], zoom: [{
                type: Input
            }], minZoom: [{
                type: Input
            }], maxZoom: [{
                type: Input
            }], projection: [{
                type: Input
            }], layersLoaded$: [{
                type: Input
            }], customControls: [{
                type: Input
            }], generateLegend: [{
                type: Input
            }], mapButtons: [{
                type: Input
            }], isButtonGroupInitiallyOpen: [{
                type: Input
            }], buttonGroupTitle: [{
                type: Input
            }], config: [{
                type: Input
            }], addActionButton: [{
                type: Input
            }], legendLabel: [{
                type: Input
            }], layerVisibilityChanged: [{
                type: Output
            }], buttonEvent: [{
                type: Output
            }], zoomChanged: [{
                type: Output
            }], extent: [{
                type: Output
            }], 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

class ProjectionToNumberPipe {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    transform(value, args) {
        return Projection[value].replace('_', '');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ProjectionToNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.7", ngImport: i0, type: ProjectionToNumberPipe, name: "projectionToNumber" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ProjectionToNumberPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'projectionToNumber' }]
        }] });

class ProjectionToEpsgPipe {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    transform(value, args) {
        return Projection[value].replace('_', 'EPSG:');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ProjectionToEpsgPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.7", ngImport: i0, type: ProjectionToEpsgPipe, name: "projectionToEpsg" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ProjectionToEpsgPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'projectionToEpsg' }]
        }] });

/**
 * Exposes methods to read and write and convert features
 */
class FeatureService {
    /**
     * Converts esrijson objects to ol feature array objects
     *
     * @param json json array from mapserver result
     */
    esriJSONToFeatures(json) {
        const result = new Array();
        for (let jsonIndex = 0; jsonIndex < json.length; jsonIndex++) {
            const element = json[jsonIndex];
            const olFeatures = new EsriJSON().readFeatures(element);
            for (let featureIndex = 0; featureIndex < olFeatures.length; featureIndex++) {
                const olFeature = olFeatures[featureIndex];
                result.push(olFeature);
            }
        }
        return result;
    }
    /**
     * Converts ol feature array to ANB Extent object
     *
     * @param olFeatures
     */
    extentFromFeatures(olFeatures) {
        return new Extent({ extent: olFeatures[0].getGeometry().getExtent() });
    }
    featureToWkt(olFeature) {
        const writer = new WKT();
        const wkt = writer.writeFeature(olFeature);
        return wkt;
    }
    featureToGeoJSON(feature) {
        const jsonString = new GeoJSON().writeFeature(feature);
        const json = JSON.parse(jsonString);
        return json;
    }
    featuresToGeoJSON(features) {
        const jsonString = new GeoJSON().writeFeatures(features.getArray());
        const json = JSON.parse(jsonString);
        return json;
    }
    geoJSONToFeature(geoJSON) {
        const feature = new GeoJSON().readFeature(geoJSON);
        return feature;
    }
    geoJSONToFeatures(geoJSON) {
        const features = new GeoJSON().readFeatures(geoJSON);
        return features;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FeatureService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FeatureService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FeatureService, decorators: [{
            type: Injectable
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Exposes methods to calculate or get the extent
 *
 *
 * ExtentService
 */
class ExtentService {
    constructor(featureService) {
        this.featureService = featureService;
    }
    /**
     * Substracts the extents of jsons and joins it as one calucalated extent
     * Returns ANB Extent object
     *
     * @param json Json array from the mapserver
     */
    getExtentFromMapServerQueryExtentResult(json) {
        const extent = new Extent({ extent: [json[0].extent.xmin, json[0].extent.ymin, json[0].extent.xmax, json[0].extent.ymax] });
        for (let index = 1; index < json.length; index++) {
            const element = json[index];
            const nextExtent = new Extent({
                extent: [element[0].extent.xmin, element[0].extent.ymin, element[0].extent.xmax, element[0].extent.ymax],
            });
            extend(extent.getExtent(), nextExtent.getExtent());
        }
        const finalExtent = new Extent({ extent: [extent[0], extent[1], extent[2], extent[3]] });
        return finalExtent;
    }
    /**
     * Substracts the features from the mapserver json results and calculates and joins the extent of the features
     * Returns ANB Extent object
     *
     * @param json Json array from the mapserver
     */
    getExtentFromMapServerQueryFeatureResult(json) {
        const olFeatures = this.featureService.esriJSONToFeatures(json);
        const olExtent = olFeatures[0].getGeometry().getExtent();
        for (let index = 1; index < olFeatures.length; index++) {
            const element = olFeatures[index];
            const nextOlExtent = element.getGeometry().getExtent();
            extend(olExtent, nextOlExtent);
        }
        const finalExtent = new Extent({ extent: [olExtent[0], olExtent[1], olExtent[2], olExtent[3]] });
        return finalExtent;
    }
    getMapExtent(map) {
        const currentExtent = map.getView().calculateExtent(map.getSize());
        const anbExtent = new Extent({ extent: [currentExtent[0], currentExtent[1], currentExtent[2], currentExtent[3]] });
        return anbExtent;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ExtentService, deps: [{ token: FeatureService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ExtentService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ExtentService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FeatureService }]; } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class VectorService {
    addFeature(layer, feature) {
        layer.getSource().addFeature(feature);
    }
    addFeatures(layer, features) {
        layer.getSource().addFeatures(features);
    }
    getFeatureById(layer, featureId) {
        const feature = layer.getSource().getFeatureById(featureId);
        return feature;
    }
    deleteFeature(layer, feature) {
        layer.getSource().removeFeature(feature);
    }
    addSearchCoordinatesFeature(layer, feature) {
        const existingFeature = this.getFeatureById(layer, 'searchedcoordinates');
        if (existingFeature) {
            this.deleteFeature(layer, existingFeature);
        }
        this.addFeature(layer, feature);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: VectorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: VectorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: VectorService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class MapModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: MapModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.7", ngImport: i0, type: MapModule, declarations: [OlMapComponent, ProjectionToNumberPipe, ProjectionToEpsgPipe], imports: [CommonModule, AnbUIElementsModule, FontAwesomeModule], exports: [OlMapComponent, ProjectionToNumberPipe, ProjectionToEpsgPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: MapModule, providers: [ZoomService, LoaderService, ProjectionToNumberPipe, ProjectionToEpsgPipe, FeatureService, ExtentService, VectorService], imports: [CommonModule, AnbUIElementsModule, FontAwesomeModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: MapModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, AnbUIElementsModule, FontAwesomeModule],
                    declarations: [OlMapComponent, ProjectionToNumberPipe, ProjectionToEpsgPipe],
                    exports: [OlMapComponent, ProjectionToNumberPipe, ProjectionToEpsgPipe],
                    providers: [ZoomService, LoaderService, ProjectionToNumberPipe, ProjectionToEpsgPipe, FeatureService, ExtentService, VectorService],
                }]
        }] });

var EsriExportMapImageFormat;
(function (EsriExportMapImageFormat) {
    EsriExportMapImageFormat["PNG"] = "png";
    EsriExportMapImageFormat["PNG8"] = "png8";
    EsriExportMapImageFormat["PNG24"] = "png24";
    EsriExportMapImageFormat["JPG"] = "jpg";
    EsriExportMapImageFormat["PDF"] = "pdf";
    EsriExportMapImageFormat["BMP"] = "bmp";
    EsriExportMapImageFormat["GIF"] = "gif";
    EsriExportMapImageFormat["SVG"] = "svg";
    EsriExportMapImageFormat["SVGZ"] = "svgz";
    EsriExportMapImageFormat["PS"] = "ps";
    EsriExportMapImageFormat["PNG32"] = "png32";
})(EsriExportMapImageFormat || (EsriExportMapImageFormat = {}));

var EsriExportMapResponseFormat;
(function (EsriExportMapResponseFormat) {
    EsriExportMapResponseFormat["HTML"] = "html";
    EsriExportMapResponseFormat["JSON"] = "json";
    EsriExportMapResponseFormat["IMAGE"] = "image";
    EsriExportMapResponseFormat["KMZ"] = "kmz";
})(EsriExportMapResponseFormat || (EsriExportMapResponseFormat = {}));

class EsriExportMapParams {
    constructor(restUrl) {
        this.restUrl = restUrl;
        /**
         * The format of the exported image. The default format is .png.
         * Values: png | png8 | png24 | jpg | pdf | bmp | gif | svg | svgz | emf | ps | png32
         *
         * https://developers.arcgis.com/rest/services-reference/export-map.htm
         *
         *  {EsriExportMapImageFormat}
         * @memberof EsriExportMapParams
         */
        this.format = EsriExportMapImageFormat.PNG;
        /**
         * If true, the image will be exported with the background color of the map set as
         * its transparent color. The default is false. Only the .png and .gif formats support transparency.
         * Internet Explorer 6 does not display transparency correctly for png24 image formats.
         * Values: true | false
         *
         * https://developers.arcgis.com/rest/services-reference/export-map.htm
         *
         * @memberof EsriExportMapParams
         */
        this.transparent = false;
        /**
         * The device resolution of the exported image (dots per inch). If the dpi is not specified,
         * an image with a default dpi of 96 will be exported.
         * Example: dpi=200
         *
         * https://developers.arcgis.com/rest/services-reference/export-map.htm
         *
         * @memberof EsriExportMapParams
         */
        this.dpi = 96;
        /**
         * The response format. The default response format is html.
         * If the format is image, the image bytes are directly streamed to the client.
         * Values: html | json | image | kmz
         *
         *  {EsriExportMapResponseFormat}
         * @memberof EsriExportMapParams
         */
        this.f = EsriExportMapResponseFormat.IMAGE;
    }
}

var QueryFormat;
(function (QueryFormat) {
    QueryFormat["HTML"] = "html";
    QueryFormat["JSON"] = "json";
    QueryFormat["PJSON"] = "pjson";
    QueryFormat["GEOJSON"] = "geojson";
    QueryFormat["KMZ"] = "kmz";
    QueryFormat["PBF"] = "pbf";
})(QueryFormat || (QueryFormat = {}));

var EsriGeometryType;
(function (EsriGeometryType) {
    EsriGeometryType["ESRIGEOMTRYPOINT"] = "esriGeometryPoint";
    EsriGeometryType["ESRIGEOMETRYMULTIPOINT"] = "esriGeometryMultipoint";
    EsriGeometryType["ESRIGEMOTRYPOLYLINE"] = "esriGeometryPolyline";
    EsriGeometryType["ESRIGEOMETRYPOLYGON"] = "esriGeometryPolygon";
    EsriGeometryType["ESRIGEOMETRYENVELOPE"] = "esriGeometryEnvelope";
})(EsriGeometryType || (EsriGeometryType = {}));

class EsriMapServerQuery {
    constructor(restUrl) {
        this.restUrl = restUrl;
        /**
         * The response format. The default response format is HTML. Protocol buffer (pbf) format is only supported
         * when the supportedQueryFormat property on the layer includes pbf.
         * Note: f=kmz would always return z-values.
         * Values: html | json | geojson | kmz | pbf (default, when returnIdsOnly=false and returnCountOnly=false)
         * Values: html | json (when outStatistics is specified)
         * Values: html | json | geojson | pbf (when either returnIdsOnly=true or returnCountOnly=true is specified)
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         *  {QueryFormat}
         * @memberof EsriMapServerQuery
         */
        this.f = QueryFormat.PJSON;
        /**
         * A literal search text. If the layer has a display field associated with it,
         * the server searches for this text in this field. This parameter is shorthand for a WHERE clause of
         * where <displayField> like '%<text>%'.
         * The text is case sensitive. This parameter is ignored if the WHERE parameter is specified.
         * Example: text=Los
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.text = '';
        /**
         * The geometry to apply as the spatial filter.
         * The structure of the geometry is the same as the structure of the JSON geometry objects returned
         * by the ArcGIS REST API. In addition to the JSON structures,
         * for envelopes and points, you can specify the geometry with a simpler comma-separated syntax.
         * Syntax:
         * geometryType=esriGeometryEnvelope&geometry={xmin: -104, ymin: 35.6, xmax: -94.32, ymax: 41}
         * geometryType=esriGeometryEnvelope&geometry=-104,35.6,-94.32,41
         * geometryType=esriGeometryPoint&geometry=-104,35.6
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.geometry = '';
        /**
         * The type of geometry specified by the geometry parameter.
         * The geometry type can be an envelope, point, line, or polygon. The default geometry type is an envelope.
         * Values: esriGeometryPoint | esriGeometryMultipoint | esriGeometryPolyline | esriGeometryPolygon | esriGeometryEnvelope
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         *  {''}
         * @memberof EsriMapServerQuery
         */
        this.geometryType = EsriGeometryType.ESRIGEOMETRYENVELOPE;
        /**
         * The spatial reference of the input geometry.
         * The spatial reference can be specified as either a well-known ID or
         * as a spatial reference JSON object. If the inSR is not specified,
         * the geometry is assumed to be in the spatial reference of the map.
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         *  {Projection}
         * @memberof EsriMapServerQuery
         */
        // eslint-disable-next-line no-underscore-dangle
        this.inSR = Projection._31370;
        /**
         * The spatial relationship to be applied on the input geometry while performing the query.
         * The supported spatial relationships include intersects, contains, envelope intersects, within, and so on.
         * The default spatial relationship is intersects (esriSpatialRelIntersects).
         * Values:
         *  esriSpatialRelIntersects |
         *  esriSpatialRelContains |
         *  sriSpatialRelCrosses |
         *  esriSpatialRelEnvelopeIntersects |
         *  esriSpatialRelIndexIntersects |
         *  esriSpatialRelOverlaps |
         *  esriSpatialRelTouches |
         *  esriSpatialRelWithin |
         *  esriSpatialRelRelation
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         *  {ESRISpatialRelationship}
         * @memberof EsriMapServerQuery
         */
        this.spatialRel = ESRISpatialRelationship.INTERSECTS;
        /**
         * The spatial relate function that can be applied while performing the query operation.
         * An example for this spatial relate function is "FFFTTT***."
         * For more information on this spatial relate function, refer to the documentation for the spatial relate function.
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.relationParam = '';
        /**
         * The object IDs of this layer or table to be queried.
         * Note:
         * There might be a drop in performance if the layer/table data source resides in an enterprise geodatabase and
         * more than 1,000 objectIds are specified.
         * Syntax: objectIds=<objectId1>, <objectId2>
         * Example: objectIds=37, 462
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.objectIds = '';
        /**
         * The time instant or the time extent to query.
         * Time instant:
         * Syntax: time=<timeInstant>
         * Example: time=1199145600000 (1 Jan 2008 00:00:00 GMT)
         * Time extent:
         * Syntax: time=<startTime>, <endTime>
         * Example: time=1199145600000, 1230768000000 (1 Jan 2008 00:00:00 GMT to 1 Jan 2009 00:00:00 GMT)
         * A null value specified for start time or end time will represent infinity for start or end time, respectively.
         *
         *  https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.time = '';
        /**
         * The list of fields to be included in the returned result set.
         * This list is a comma-delimited list of field names.
         * If you specify the shape field in the list of return fields, it is ignored.
         * To request geometry, set returnGeometry to true.
         * You can also specify the wildcard "*" as the value of this parameter.
         * In this case, the query results include all the field values.
         * Example: outFields=AREANAME,ST,POP2000
         * Example (wildcard usage): outFields=*
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.outFields = '*';
        /**
         * If true, the result set includes the geometry associated with each result. The default is true.
         * Values: true | false
         * Note:
         * This parameter cannot be used with returnDistinctValues.
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.returnGeometry = true;
        /**
         * This option can be used to specify the maxAllowableOffset to be used for generalizing geometries returned by the query operation.
         * The maxAllowableOffset is in the units of the outSR.
         * If outSR is not specified, maxAllowableOffset is assumed to be in the unit of the spatial reference of the map.
         * Example: maxAllowableOffset=2
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.maxAllowableOffset = '';
        /**
         * This option can be used to specify the number of decimal places in the response geometries returned by the query operation.
         * This applies to x- and y-values only (not m- or z-values).
         * Example: geometryPrecision=3
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.geometryPrecision = '';
        /**
         * The spatial reference of the returned geometry.
         * The spatial reference can be specified as either a well-known ID or as a spatial reference JSON object.
         * If outSR is not specified, the geometry is returned in the spatial reference of the map.
         * when using outSR with pbf, the pbf format will use coordinate quantization for layer queries.
         * When an output spatial reference is not provided for a query operation,
         * the Map Service derives coordinate quantization parameters from the layer's spatial reference.
         * If the precision in the layer's spatial references is inadequate for the client application's use,
         * it should pass in a spatial reference with suitable precision as the output spatial reference.
         * If the layer's source spatial reference has the desired precision and it is suitable for the client's use,
         * the client can use the source layer's spatial reference as the output spatial reference.
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         *  {Projection}
         * @memberof EsriMapServerQuery
         */
        // eslint-disable-next-line no-underscore-dangle
        this.outSR = Projection._31370;
        /**
         * If true, the response only includes an array of object IDs. Otherwise the response is a feature set. The default is false.
         * Note that while there is a limit on the number of features included in the feature set response,
         * there is no limit on the number of object IDs returned in the ID array response.
         * Clients can exploit this to get all the query conforming object IDs by specifying returnIdsOnly=true
         * and subsequently requesting feature sets for subsets of object IDs.
         * Values: false | true
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.returnIdsOnly = false;
        /**
         * If true, the response only includes the count
         * (number of features/records) that would be returned by a query.
         * Otherwise the response is a feature set. The default is false. This option supersedes the returnIdsOnly parameter.
         * Values: false | true
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.returnCountOnly = false;
        /**
         * One or more field names or expressions that the features/records need to be ordered by.
         * Use ASC or DESC for ascending or descending order, respectively.
         * Syntax: orderByFields=expression1 <ORDER>, expression2 <ORDER>, expression3 <ORDER>
         * Example: orderByFields=STATE_NAME ASC, RACE DESC, GENDER
         * Example: orderByFields=POPULATION / SHAPE_AREA
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.orderByFields = '';
        /**
         * The definitions for one or more field-based statistics to be calculated.
         * Note:
         * outStatistics is supported on only those layers/tables that indicate supportsStatistics is true.
         * If outStatisticFieldName is empty or missing, the map server would assign a field name to the returned statistic field.
         * A valid field name can only contain alphanumeric characters and an underscore.
         * When outStatistics, the only other parameters that will be used are groupByFieldsForStatistics,
         * orderByFields, text, time, and where.
         * At version 10.1, specifying geometry, objectIds, or gdbVersion parameters would return an error.
         * At version 10.1 SP1, support for the geometry parameter was added.
         * At version 10.2, support for the gdbVersion parameter was added.
         * Values: An array of statistic definitions.
         * A statistic definition specifies the type of statistic, the field on which it is to be calculated,
         * and the resulting output field name.
         * Syntax:
         * [
         *   {
         *     "statisticType": "<count | sum | min | max | avg | stddev | var>",
         *     "onStatisticField": "Field1",
         *     "outStatisticFieldName": "Out_Field_Name1"
         *   },
         *   {
         *     "statisticType": "<count | sum | min | max | avg | stddev | var>",
         *     "onStatisticField": "Field2",
         *     "outStatisticFieldName": "Out_Field_Name2"
         *   }
         * ]
         * Example:
         *
         * [
         *   {
         *     "statisticType": "sum",
         *     "onStatisticField": "GENDER",
         *     "outStatisticFieldName": "PopulationByGender"
         *   },
         *   {
         *     "statisticType": "avg",
         *     "onStatisticField": "INCOME",
         *     "outStatisticFieldName": "AverageIncome"
         *   }
         * ]
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.outStatistics = '';
        /**
         * One or more field names using the values that need to be grouped for calculating the statistics.
         * Note:
         * At 10.4, expressions are allowed in addition to field name. When StandardizedQueries is enabled,
         * only expressions that conform to the specifications are allowed.
         * When StandardizedQueries is disabled, you can pass in any expression that the underlying database allows.
         * groupByFieldsForStatistics is valid only when the outStatistics parameter is used.
         * Syntax: groupByFieldsForStatistics=expression1, expression2
         * Example: groupByFieldsForStatistics=STATE_NAME, GENDER
         * Example: groupByFieldsForStatistics=extract(month from incident_date) to group by month when StandardizedQueries is enabled.
         *
         *  https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.groupByFieldsForStatistics = '';
        /**
         * If true, the z-values will be included in the results if the features have z-values.
         * Otherwise z-values are not returned. The default is false.
         * Note:
         * This parameter only applies if returnGeometry is true.
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.returnZ = false;
        /**
         * If true, m-values will be included in the results if the features have m-values.
         * Otherwise, m-values are not returned. The default is false
         * Note:
         * This parameter only applies if returnGeometry is true.
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.returnM = false;
        /**
         * GeoDatabase version to query.
         * This parameter applies only if the hasVersionedData property of the service and
         * the isDataVersioned property of the layers queried are true.
         * If this is not specified, the query will apply to the published map's version.
         * Syntax: gdbVersion=<geodatabase version>
         * Example: gdbVersion=sde.USER1
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.gdbVersion = '';
        /**
         * If true, returns distinct values based on the fields specified in outFields.
         * This parameter applies only if the supportsAdvancedQueries property of the layer is true.
         * Syntax: returnDistinctValues=<true | false>
         * Example: returnDistinctValues=true
         * Note:
         * This parameter cannot be used when returnGeometry is true.
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.resturnDistinctValues = false;
        /**
         * If true, returns true curves in output geometries; otherwise, curves are converted to densified polylines or polygons.
         * Syntax: returnTrueCurves=<true | false>
         * Example: returnTrueCurves=true
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.returnTrueCurves = false;
        /**
         * This option can be used for fetching query results by skipping the specified number
         * of records and starts from the next record (for example, resultOffset + 1th). The Default is 0.
         * This parameter only applies if supportsPagination is true.
         * You can use this option to fetch records that are beyond maxRecordCount.
         * For example, if maxRecordCount is 1,000, you can get the next 100 records by
         * setting resultOffset=1000 and resultRecordCount = 100; query results can return the results in the range of 1,001 to 1,100.
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.resultOffset = '';
        /**
         * This option can be used for fetching query results up to the resultRecordCount specified.
         * When resultOffset is specified but this parameter is not, the map service defaults it to maxRecordCount.
         * The maximum value for this parameter is the value of the layer's maxRecordCount property.
         * This parameter only applies if supportsPagination is true.
         * Example: resultRecordCount=10 to fetch up to 10 records
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *
         * @memberof EsriMapServerQuery
         */
        this.resultRecordCount = '';
        /**
         * Use this parameter to apply a datum transformation while projecting geometries
         * in the results when outSR is different than the layer's spatial reference.
         * Note: while specifying transformation, you need to think about which datum transformation
         * best applicable to project the layer (not the map service) to the outSR. sourceSpatialReference property
         *  in the layer resource reports which spatial reference features are stored in the source dataset.
         * For a list of valid datum transformation ID values and well-known text strings, see Geographic transformations.
         * Syntax: datumTransformation=wkid1.
         * Examples: datumTransformation=1623 to apply a transformation.
         * Syntax: datumTransformation={<dt1>}.
         * Examples:
         * datumTransformation={"geoTransforms":[{"wkid":108889,"transformForward":true},{"wkid":1622,"transformForward":false}]}
         * to apply a composite transformation.
         * For more information on datum transformation, please see transformation parameter in Project operation.
         *
         * https://developers.arcgis.com/rest/services-reference/query-map-service-layer-.htm
         *      *
         * @memberof EsriMapServerQuery
         */
        this.datumTransformation = '';
    }
}

class ButtonModel {
}

class ANBLayer {
    constructor(id, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layer) {
        this.id = id;
        this.layer = layer;
        layer.set('id', id);
    }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
class ArcgisQueryService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    mapServerQueryGET(query) {
        if (!query) {
            return;
        }
        const url = `${query.restUrl}/query?` +
            `&f=${query.f.toString()}` +
            `&text=${query.text}` +
            `&geometry=${query.geometry}` +
            `&geometryType=${query.geometryType.toString()}` +
            `&inSR=${query.inSR ? query.inSR.replace('_', '') : ''}` +
            `&spatialRel=${query.spatialRel.toString()}` +
            `&relationParam=${query.relationParam}` +
            `where=${query.where}` +
            `&objectIds=${query.objectIds}` +
            `&time=${query.time}` +
            `&distance=${query.distance}` +
            `&units=${query.units}` +
            `&outFields=${query.outFields}` +
            `&returnGeometry=${query.returnGeometry}` +
            `&maxAllowableOffset=${query.maxAllowableOffset}` +
            `&geometryPrecision=${query.geometryPrecision}` +
            `&outSR=${query.outSR ? query.outSR.replace('_', '') : ''}` +
            `&returnIdsOnly=${query.returnIdsOnly}` +
            `&returnCountOnly=${query.returnCountOnly}` +
            `&returnExtentOnly=${query.returnExtentOnly}` +
            `&orderByFields=${query.orderByFields}` +
            `&outStatistics=${query.outStatistics}` +
            `&groupByFieldsForStatistics=${query.groupByFieldsForStatistics}` +
            `&returnZ=${query.returnZ}` +
            `&returnM=${query.returnM}` +
            `&gdbVersion=${query.gdbVersion}` +
            `&returnDistinctValues=${query.resturnDistinctValues}` +
            `&returnTrueCurves=${query.returnTrueCurves}` +
            `&resultOffset=${query.resultOffset}` +
            `&resultRecordCount=${query.resultRecordCount}` +
            `&datumTransformation=${query.datumTransformation}` +
            `&rangeValues=${query.rangeValues}`;
        return this.httpClient.get(url);
    }
    mapServerQueryPOST(query) {
        if (!query) {
            return;
        }
        return this.httpClient.post(query.restUrl, query);
    }
    exportMapGet(params) {
        return new ImageArcGISRest({
            ratio: 1,
            params: {
                FORMAT: params.format,
                GDBVERSION: params.gdbVersion,
                HISTORICMOMENT: params.historicMoment,
                IMAGESR: params.imageSR,
                LAYERDEFS: params.layerDefs,
                LAYERTIMEOPTIONS: params.layerTimeOptions,
                LAYERS: params.layers,
                MAPSCALE: params.mapScale,
                SIZE: params.size,
                TIME: params.time,
                TRANSPARENT: params.transparent,
            },
            url: params.restUrl,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ArcgisQueryService, deps: [{ token: i4.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ArcgisQueryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: ArcgisQueryService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i4.HttpClient }]; } });

/*
 * Public API Surface of anb-ng-maps
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ANBDragBox, ANBDraw, ANBLayer, ANBSelect, ArcgisQueryService, ButtonModel, ButtonStatus, EsriExportMapParams, EsriMapServerQuery, ExtentService, FeatureService, LoaderService, MapModule, OlMapComponent, Projection, ProjectionToEpsgPipe, ProjectionToNumberPipe, ESRISpatialRelationship as SpatialRelationship, VectorService, ZoomService };
